import React, { useEffect, useRef } from "react";
import Lottie from "lottie-react";
import progressBar from "assets/animations/bot/circular.json";
import novaAnimation from "assets/animations/bot/nova.json";
import orionAnimation from "assets/animations/bot/orion.json";
import lyraAnimation from "assets/animations/bot/lyra.json";
import vegaAnimation from "assets/animations/bot/vega.json";

// ────────────────────────────────
// Voiceover Helper
// ────────────────────────────────
const speakText = (text, muted) => {
  if (muted || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-US";
  utterance.pitch = 1;
  utterance.rate = 1.05;
  utterance.volume = 1;
  window.speechSynthesis.speak(utterance);
};

const BotLoader = ({ size = 400, botStatus, muted, prepaid = false }) => {
  // ✅ Track which bot/status combo already spoke
  const spokenRef = useRef({});

  useEffect(() => {
    if (!botStatus) return;

    const bot = botStatus.bot_number;
    const status = botStatus.status;

    if (!bot || !status) return;

    // ✅ If bot changed → reset memory for past statuses
    if (!spokenRef.current[bot]) {
      spokenRef.current[bot] = {};
    }

    // ❌ If status already spoken for this bot → do nothing
    if (spokenRef.current[bot][status]) return;

    // ✅ Mark spoken for this bot+status
    spokenRef.current[bot][status] = true;

    // ✅ Bot 1 (Nova)
    if (bot === 1) {
      if (status === "pending") {
        speakText(
          prepaid
            ? "I am getting and reading your pdf invoices."
            : "I’m preparing your vendor costing data. And sorting your information by arrival port date.",
          muted
        );
      } else if (status === "in progress") {
        speakText(
          prepaid
            ? "I am verifing storage and late fee."
            : "I’m verifying if client costing is performed or not.",
          muted
        );
      } else if (status === "completed" || status === "failed") {
        speakText(
          prepaid
            ? "Your invoice processing is completed. Now handing over the data to Agent ChargeMaster for storage and late fee prepaid charges."
            : "Your vendor costing preparation is complete. Now handing over the data to Orion for invoice processing.",
          muted
        );
      }
    }

    // ✅ Bot 2 (Orion)
    else if (bot === 2) {
      if (status === "in progress" || status === "partial") {
        speakText(
          prepaid
            ? "I am validating storage and late fee for prepaid charges."
            : `I'm processing your invoice ${botStatus?.invoice_no} from vendor ${botStatus?.vendor}.`,
          muted
        );
      } else if (status === "completed" || status === "failed") {
        speakText(
          prepaid
            ? `Your prepaid charges execustion for vin ${botStatus?.vin} is completed.`
            : `I have processed ${botStatus?.uniquevendor} vendor types, ${botStatus?.uniquevendor} vendor names,
          ${botStatus?.uniquecontainer} unique containers, and ${botStatus?.uniquevin} unique VINs.
          Now handing over to NovaX to perform vendor costing`,
          muted
        );
      }
    }

    // ✅ Bot 4 (NovaX)
    else if (!prepaid && bot === 4) {
      if (status === "in progress") {
        speakText("I’m Lyra. I’ll now perform your vendor costings.", muted);
      } else if (status === "completed" || status === "failed") {
        speakText("Vendor costing has been successfully updated.", muted);
      }
    }
  }, [botStatus, muted, prepaid]);

  // Select animation dynamically
  const getAnimation = () => {
    switch (botStatus?.bot_number) {
      case 1:
        return novaAnimation;
      case 2:
        return orionAnimation;
      case 4:
        return lyraAnimation;
      case 5:
        return vegaAnimation;
      default:
        return lyraAnimation;
    }
  };

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Circular Progress Animation */}
      <Lottie
        animationData={progressBar}
        loop
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
      />

      {/* Bot Animation */}
      <Lottie
        animationData={getAnimation()}
        loop
        style={{
          width: size * 0.6,
          height: size * 0.6,
          zIndex: 1,
        }}
      />
    </div>
  );
};

export default BotLoader;
